// DemoTapBackground.tsx - Lively Interactive Tap Demo
import React, { useEffect, useRef, useState } from 'react';
import UfoIcon from '../../components/flying-object/UfoIcon';
import { particleEffectsService, TapParticle, BurstParticle } from '../../services/particleEffectsService';
import TapParticles from '../../components/common/TapParticles';
import BurstParticles from '../../components/common/BurstParticles';

// Multiple demo paths for different UFOs - slower timing
const demoPaths = [
  // Main UFO path (target for tapping) - slower
  [
  { x: 0.15, y: 0.7, time: 0 },
    { x: 0.25, y: 0.3, time: 1600 },
    { x: 0.5, y: 0.15, time: 3200 },
    { x: 0.75, y: 0.3, time: 4800 },
    { x: 0.85, y: 0.7, time: 6400 },
    { x: 0.5, y: 0.85, time: 8000 },
    { x: 0.15, y: 0.7, time: 9600 },
  ],
  // Secondary UFO path - slower
  [
    { x: 0.8, y: 0.2, time: 0 },
    { x: 0.6, y: 0.6, time: 2400 },
    { x: 0.3, y: 0.4, time: 4800 },
    { x: 0.2, y: 0.8, time: 7200 },
    { x: 0.7, y: 0.9, time: 9600 },
    { x: 0.9, y: 0.5, time: 12000 },
    { x: 0.8, y: 0.2, time: 14400 },
  ],
  // Fast scout UFO - slower
  [
    { x: 0.1, y: 0.1, time: 0 },
    { x: 0.9, y: 0.1, time: 2000 },
    { x: 0.9, y: 0.9, time: 4000 },
    { x: 0.1, y: 0.9, time: 6000 },
    { x: 0.1, y: 0.1, time: 8000 },
  ]
];

const TAP_INTERVAL = 1500; // ms between tap demos (reduced)
const TAP_DURATION = 800; // ms tap effect visible (reduced)

const emojiTypes = ['🐥', '🐱', '👼', '🦄', '🎶', '❤️‍🔥'];

const DemoTapBackground: React.FC = () => {
  // Multiple UFOs with fixed base scales
  const [ufos, setUfos] = useState([
    { x: 0, y: 0, rotation: 0, pathIndex: 0, emoji: '🐥', ufoType: 'basic' as const, baseScale: 1.5, glow: '#FFC107', isTarget: true },
    { x: 0, y: 0, rotation: 0, pathIndex: 1, emoji: '🐱', ufoType: 'armored' as const, baseScale: 1.2, glow: '#E91E63', isTarget: false },
    { x: 0, y: 0, rotation: 0, pathIndex: 2, emoji: '🦁', ufoType: 'stealth' as const, baseScale: 1.0, glow: '#00BCD4', isTarget: false },
  ]);
  
  const [showTap, setShowTap] = useState(false);
  const [tapParticles, setTapParticles] = useState<TapParticle[]>([]);
  const [burstParticles, setBurstParticles] = useState<BurstParticle[]>([]);
  const [floatingEmojis, setFloatingEmojis] = useState<TapParticle[]>([]);
  
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const comboTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const particleIdRef = useRef(0);
  const lastRescueRef = useRef(0);
  const lastTapTimeRef = useRef(0);

  // Easing function for smooth movement
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Create floating rescued emoji
  const createFloatingEmoji = (x: number, y: number) => {
    const emoji = emojiTypes[Math.floor(Math.random() * emojiTypes.length)];
    return {
      id: particleIdRef.current++,
      x: x + (Math.random() - 0.5) * 0.1,
      y: y + (Math.random() - 0.5) * 0.1,
      vx: (Math.random() - 0.5) * 0.001,
      vy: -Math.random() * 0.002 - 0.001,
      life: 0,
      maxLife: 3000 + Math.random() * 2000,
      emoji,
      scale: 1,
    };
  };

  // Animate UFOs and effects
  useEffect(() => {
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      
      // Update all UFOs with proper path movement
      setUfos(prevUfos => prevUfos.map((ufo, index) => {
        const path = demoPaths[ufo.pathIndex];
        const pathDuration = path[path.length - 1].time;
        const pathElapsed = elapsed % pathDuration;
        
        // Find current path segment
      let idx = 0;
        for (let i = 0; i < path.length - 1; i++) {
          if (pathElapsed >= path[i].time && pathElapsed < path[i + 1].time) {
            idx = i;
            break;
          }
        }
        
        const p1 = path[idx];
        const p2 = path[idx + 1] || path[0];
      const segTime = p2.time - p1.time;
        const t = segTime > 0 ? easeInOutCubic((pathElapsed - p1.time) / segTime) : 0;
        
        // Calculate position along path
      const x = p1.x + (p2.x - p1.x) * t;
      const y = p1.y + (p2.y - p1.y) * t;
        const angle = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI) + 90;
        
        // Add subtle floating motion
        const floatOffset = Math.sin(elapsed * 0.003 + index * 2) * 0.015;
        const bobOffset = Math.cos(elapsed * 0.004 + index) * 0.01;
        
        return {
          ...ufo,
          x: x + floatOffset,
          y: y + bobOffset,
          rotation: angle,
        };
      }));

      // Handle tap timing - simple interval-based approach
      const targetUfo = ufos.find(ufo => ufo.isTarget);
      if (targetUfo && elapsed - lastTapTimeRef.current > TAP_INTERVAL) {
        setShowTap(true);
        lastTapTimeRef.current = elapsed;
        
        // Get the exact current position of target UFO
        const path = demoPaths[targetUfo.pathIndex];
        const pathDuration = path[path.length - 1].time;
        const pathElapsed = elapsed % pathDuration;
        
        // Find current path segment for target UFO
        let idx = 0;
        for (let i = 0; i < path.length - 1; i++) {
          if (pathElapsed >= path[i].time && pathElapsed < path[i + 1].time) {
            idx = i;
            break;
          }
        }
        
        const p1 = path[idx];
        const p2 = path[idx + 1] || path[0];
        const segTime = p2.time - p1.time;
        const t = segTime > 0 ? easeInOutCubic((pathElapsed - p1.time) / segTime) : 0;
        
        // Calculate exact position along path
        const exactX = p1.x + (p2.x - p1.x) * t;
        const exactY = p1.y + (p2.y - p1.y) * t;
        
        // Add floating motion to match UFO
        const floatOffset = Math.sin(elapsed * 0.003) * 0.015;
        const bobOffset = Math.cos(elapsed * 0.004) * 0.01;
        const finalX = exactX + floatOffset;
        const finalY = exactY + bobOffset;
        
        // Create particles at exact target UFO position
        setTapParticles(prev => [
          ...prev.slice(-6), // Keep recent particles
          ...particleEffectsService.createTapParticles(finalX, finalY, 1),
        ]);
        
        // Create burst particles at exact tap location (every 3rd tap)
        const tapCount = Math.floor(elapsed / TAP_INTERVAL);
        if (tapCount % 3 === 0) {
          setBurstParticles(prevBurst => [
            ...prevBurst.slice(-4),
            ...particleEffectsService.createBurstParticles(finalX, finalY),
          ]);
        }
        
        // Hide tap after duration
        if (tapTimeoutRef.current) clearTimeout(tapTimeoutRef.current);
        tapTimeoutRef.current = setTimeout(() => {
          setShowTap(false);
        }, TAP_DURATION);
      }

      // Create floating rescued emojis occasionally
      if (elapsed - lastRescueRef.current > 5000) {
        const randomUfo = ufos[Math.floor(Math.random() * ufos.length)];
        setFloatingEmojis(prev => [
          ...prev.slice(-3), // Keep only recent 3 emojis
          createFloatingEmoji(randomUfo.x, randomUfo.y),
        ]);
        lastRescueRef.current = elapsed;
      }
      
      // Update tap particles
      setTapParticles(prev => particleEffectsService.updateTapParticles(prev));
      
      // Update burst particles
      setBurstParticles(prev => particleEffectsService.updateBurstParticles(prev));

      // Update floating emojis
      setFloatingEmojis(prev => prev
        .map(emoji => ({
          ...emoji,
          x: emoji.x + emoji.vx,
          y: emoji.y + emoji.vy,
          life: emoji.life + 16,
          vy: emoji.vy + 0.00001, // Slight gravity
        }))
        .filter(emoji => emoji.life < emoji.maxLife)
      );

      requestRef.current = requestAnimationFrame(animate);
    };

    startTimeRef.current = Date.now();
    requestRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      if (tapTimeoutRef.current) clearTimeout(tapTimeoutRef.current);
      if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get the target UFO for tap positioning
  const targetUfo = ufos.find(ufo => ufo.isTarget) || ufos[0];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 100,
        opacity: 0.9,
        transition: 'opacity 0.3s',
        overflow: 'hidden',
      }}
    >
      {/* Cosmic background elements */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(circle at 20% 30%, rgba(147, 51, 234, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 80%)
          `,
          animation: 'cosmicShift 20s infinite linear',
        }}
      />

      {/* Floating rescued emojis */}
      {floatingEmojis.map(emoji => {
        const progress = emoji.life / emoji.maxLife;
        const opacity = Math.sin(Math.PI * (1 - progress)) * 0.7;
        const scale = 1 + Math.sin(Math.PI * progress) * 0.3;
        
        return (
          <div
            key={emoji.id}
            style={{
              position: 'absolute',
              left: emoji.x * size.width,
              top: emoji.y * size.height,
              transform: `translate(-50%, -50%) scale(${scale})`,
              fontSize: '1.2rem',
              opacity,
              pointerEvents: 'none',
              zIndex: 99,
              filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.6))',
            }}
          >
            {emoji.emoji}
          </div>
        );
      })}

      {/* Tap particles */}
      <TapParticles particles={tapParticles} containerSize={0} zIndex={98} />

      {/* Burst particles for combos */}
      <BurstParticles particles={burstParticles} containerSize={0} zIndex={97} />

      {/* Multiple UFOs */}
      {ufos.map((ufo, index) => {
        // Calculate proper scale with breathing effect
        const breathingScale = ufo.baseScale + Math.sin(Date.now() * 0.005 + index) * 0.05;
        
        return (
          <div
            key={index}
              style={{
                position: 'absolute',
              left: ufo.x * size.width,
              top: ufo.y * size.height,
              transform: `translate(-50%, -50%) scale(${breathingScale}) rotate(${ufo.rotation * 0.1}deg)`,
                pointerEvents: 'none',
              filter: `drop-shadow(0 0 ${15 + index * 5}px ${ufo.glow}cc)`,
              zIndex: 101 + index,
              transition: 'filter 0.3s ease',
            }}
          >
            <UfoIcon emoji={ufo.emoji} ufoType={ufo.ufoType} />
          </div>
        );
      })}

      {/* Hand emoji tap effect - positioned above target UFO */}
      {showTap && (
        <div
          style={{
            position: 'absolute',
            left: targetUfo.x * size.width,
            top: targetUfo.y * size.height + 30, // Position above UFO
            transform: 'translate(-50%, -50%)',
            fontSize: '2.5rem', // Bigger hand emoji
            opacity: 0.9,
            pointerEvents: 'none',
            zIndex: 110,
            animation: `handTap ${TAP_DURATION}ms ease-out`,
          }}
        >
          <span role="img" aria-label="tap">👆</span>
          </div>
        )}

      {/* Enhanced CSS Animations */}
      <style>{`
        @keyframes cosmicShift {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.1); }
          100% { transform: rotate(360deg) scale(1); }
        }
        
        @keyframes handTap {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5) translateY(-20px); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2) translateY(0px); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8) translateY(10px); }
        }
      `}</style>
    </div>
  );
};

export default DemoTapBackground;